import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VATReturnCard from './components/VATReturnCard';
import ComplianceAlert from './components/ComplianceAlert';
import VATCalculationSummary from './components/VATCalculationSummary';
import TransactionReviewTable from './components/TransactionReviewTable';
import AIComplianceChecker from './components/AIComplianceChecker';
import FilingStatusTracker from './components/FilingStatusTracker';
import { useAuth } from '../../contexts/AuthContext';
import complianceCenterService from '../../services/complianceCenterService';

const ComplianceCenter = () => {
  const { userProfile, isAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAICheckLoading, setIsAICheckLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Data states - initialized as empty arrays instead of mock data
  const [vatReturns, setVatReturns] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [filings, setFilings] = useState([]);
  const [vatCalculations, setVatCalculations] = useState({
    inputVAT: 0,
    outputVAT: 0,
    netLiability: 0,
    adjustments: 0,
    previousBalance: 0,
    totalDue: 0
  });

  // Loading states
  const [loadingStates, setLoadingStates] = useState({
    vatReturns: true,
    alerts: true,
    transactions: true,
    insights: false,
    filings: false,
    calculations: true
  });

  // Set loading state helper
  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Initialize service with organization ID
  useEffect(() => {
    if (userProfile?.organization_id) {
      complianceCenterService?.setOrganizationId(userProfile?.organization_id);
    }
  }, [userProfile?.organization_id]);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated && userProfile?.organization_id) {
      loadInitialData();
    }
  }, [isAuthenticated, userProfile?.organization_id]);

  const loadInitialData = async () => {
    try {
      // Load VAT returns
      setLoading('vatReturns', true);
      const vatResult = await complianceCenterService?.calculateVATReturns();
      if (!vatResult?.error) {
        setVatReturns(vatResult?.data || []);
      } else {
        console.error('Error loading VAT returns:', vatResult?.error);
        setVatReturns([]);
      }
      setLoading('vatReturns', false);

      // Load compliance alerts
      setLoading('alerts', true);
      const alertsResult = await complianceCenterService?.getComplianceAlerts();
      if (!alertsResult?.error) {
        setComplianceAlerts(alertsResult?.data || []);
      } else {
        console.error('Error loading alerts:', alertsResult?.error);
        setComplianceAlerts([]);
      }
      setLoading('alerts', false);

      // Load transactions
      setLoading('transactions', true);
      const transactionsResult = await complianceCenterService?.getTransactionsForReview();
      if (!transactionsResult?.error) {
        setTransactions(transactionsResult?.data || []);
      } else {
        console.error('Error loading transactions:', transactionsResult?.error);
        setTransactions([]);
      }
      setLoading('transactions', false);

      // Load VAT calculations for current period
      setLoading('calculations', true);
      const calculationsResult = await complianceCenterService?.calculateVATSummary();
      if (!calculationsResult?.error && calculationsResult?.data) {
        setVatCalculations(calculationsResult?.data);
      } else {
        console.error('Error loading VAT calculations:', calculationsResult?.error);
      }
      setLoading('calculations', false);

    } catch (error) {
      console.error('Error loading initial data:', error);
      // Set all loading states to false on error
      setLoadingStates({
        vatReturns: false,
        alerts: false,
        transactions: false,
        insights: false,
        filings: false,
        calculations: false
      });
    }
  };

  // Load AI insights (on demand)
  const loadAIInsights = async () => {
    if (loadingStates?.insights) return;
    
    try {
      setLoading('insights', true);
      const insightsResult = await complianceCenterService?.getAIComplianceInsights();
      if (!insightsResult?.error) {
        setAiInsights(insightsResult?.data || []);
      } else {
        console.error('Error loading AI insights:', insightsResult?.error);
        setAiInsights([]);
      }
    } catch (error) {
      console.error('Error loading AI insights:', error);
      setAiInsights([]);
    } finally {
      setLoading('insights', false);
    }
  };

  // Load filing status (on demand)
  const loadFilings = async () => {
    if (loadingStates?.filings) return;
    
    try {
      setLoading('filings', true);
      const filingsResult = await complianceCenterService?.getFilingStatus();
      if (!filingsResult?.error) {
        setFilings(filingsResult?.data || []);
      } else {
        console.error('Error loading filings:', filingsResult?.error);
        setFilings([]);
      }
    } catch (error) {
      console.error('Error loading filings:', error);
      setFilings([]);
    } finally {
      setLoading('filings', false);
    }
  };

  // Load data when switching tabs
  useEffect(() => {
    if (!isAuthenticated || !userProfile?.organization_id) return;

    if (activeTab === 'ai-checker' && aiInsights?.length === 0) {
      loadAIInsights();
    } else if (activeTab === 'filing-status' && filings?.length === 0) {
      loadFilings();
    }
  }, [activeTab, isAuthenticated, userProfile?.organization_id]);

  const showNotificationMessage = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  const handleVATReturnAction = async (returnId, action) => {
    const vatReturn = vatReturns?.find(r => r?.id === returnId);
    const returnPeriod = vatReturn?.period || 'Unknown Period';
    
    if (action === 'view') {
      showNotificationMessage(`Viewing VAT return details for ${returnPeriod}`);
      // In a real app, this would open a detailed modal or navigate to details page
      setTimeout(() => {
        console.log(`Opening detailed view for VAT return ${returnId}`);
      }, 500);
    } else if (action === 'file') {
      showNotificationMessage(`Filing VAT return for ${returnPeriod}...`);
      // Simulate filing process - in real app this would submit to tax authority
      setTimeout(() => {
        setVatReturns(prev => 
          prev?.map(vat => 
            vat?.id === returnId 
              ? { ...vat, status: 'filed' }
              : vat
          )
        );
        showNotificationMessage(`VAT return for ${returnPeriod} filed successfully!`);
      }, 2000);
    }
  };

  const handleDismissAlert = (alertId) => {
    setComplianceAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
    showNotificationMessage('Alert dismissed successfully');
  };

  const handleTransactionClick = (transaction) => {
    showNotificationMessage(`Opening transaction details: ${transaction?.reference}`);
    // In a real app, this would open transaction details modal
    setTimeout(() => {
      console.log(`Displaying transaction modal for ${transaction?.reference}`);
    }, 500);
  };

  const handleBulkAction = async (action, transactionIds) => {
    if (!Array.isArray(transactionIds) || transactionIds?.length === 0) {
      showNotificationMessage('No transactions selected');
      return;
    }

    try {
      showNotificationMessage(`Updating ${transactionIds?.length} transactions...`);
      
      const result = await complianceCenterService?.updateTransactionStatus(transactionIds, action);
      
      if (!result?.error) {
        // Update local state
        setTransactions(prev => 
          prev?.map(t => 
            transactionIds?.includes(t?.id)
              ? { ...t, status: action === 'verify' ? 'verified' : 'flagged' }
              : t
          )
        );
        showNotificationMessage(`${result?.data?.updated} transactions updated successfully`);
      } else {
        showNotificationMessage('Failed to update some transactions');
      }
    } catch (error) {
      console.error('Error updating transactions:', error);
      showNotificationMessage('Error updating transactions');
    }
  };

  const handleRunAICheck = async () => {
    setIsAICheckLoading(true);
    showNotificationMessage('Running AI compliance check...');
    
    try {
      await loadAIInsights();
      showNotificationMessage('AI compliance analysis completed successfully!');
    } catch (error) {
      showNotificationMessage('AI compliance check failed');
    } finally {
      setIsAICheckLoading(false);
    }
  };

  const handleViewInsightDetails = (insight) => {
    showNotificationMessage(`Opening insight details: ${insight?.title}`);
    // In a real app, this would open detailed insight modal
  };

  const handleViewReceipt = (filing) => {
    showNotificationMessage(`Opening receipt for filing: ${filing?.reference}`);
    // Simulate PDF download
    setTimeout(() => {
      const blob = new Blob(['Mock receipt content'], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${filing?.reference}.pdf`;
      a?.click();
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleDownloadReturn = (filing) => {
    showNotificationMessage(`Downloading VAT return for ${filing?.period}...`);
    // Simulate file download
    setTimeout(() => {
      const blob = new Blob(['Mock VAT return content'], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vat-return-${filing?.period}.pdf`;
      a?.click();
      URL.revokeObjectURL(url);
      showNotificationMessage('VAT return downloaded successfully');
    }, 1000);
  };

  const handleExportReports = () => {
    showNotificationMessage('Preparing compliance reports for export...');
    setTimeout(() => {
      const csvData = [
        ['Period', 'Status', 'Due Date', 'Net Liability'],
        ...vatReturns?.map(vat => [
          vat?.period,
          vat?.status,
          vat?.dueDate,
          vat?.netLiability
        ])
      ];
      
      const csvContent = csvData?.map(row => row?.join(','))?.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'compliance-reports.csv';
      a?.click();
      URL.revokeObjectURL(url);
      
      showNotificationMessage('Compliance reports exported successfully');
    }, 1500);
  };

  const handleNewVATReturn = () => {
    showNotificationMessage('Creating new VAT return...');
    // In a real app, this would navigate to VAT return creation form
    setTimeout(() => {
      showNotificationMessage('New VAT return creation form opened');
    }, 1000);
  };

  const handleRecalculateVAT = async () => {
    showNotificationMessage('Recalculating VAT amounts...');
    try {
      setLoading('calculations', true);
      const calculationsResult = await complianceCenterService?.calculateVATSummary();
      if (!calculationsResult?.error && calculationsResult?.data) {
        setVatCalculations(calculationsResult?.data);
        showNotificationMessage('VAT calculations updated successfully');
      } else {
        showNotificationMessage('Failed to recalculate VAT amounts');
      }
    } catch (error) {
      showNotificationMessage('Error recalculating VAT amounts');
    } finally {
      setLoading('calculations', false);
    }
  };

  // Show loading or no access states
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Authentication Required</h2>
              <p className="text-muted-foreground">Please sign in to access the compliance center.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!userProfile?.organization_id) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Building" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Organization Required</h2>
              <p className="text-muted-foreground">Please join an organization to access compliance features.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'returns', label: 'VAT Returns', icon: 'FileText' },
    { id: 'transactions', label: 'Transaction Review', icon: 'Search' },
    { id: 'ai-checker', label: 'AI Compliance', icon: 'Brain' },
    { id: 'filing-status', label: 'Filing Status', icon: 'CheckCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      {/* Notification Toast */}
      {showNotification && (
        <div className="fixed top-20 right-4 bg-card border border-border rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-right-2">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm text-foreground">{notificationMessage}</span>
          </div>
        </div>
      )}
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Compliance Center</h1>
                <p className="text-muted-foreground mt-2">
                  Streamline VAT returns, filing, and regulatory compliance
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  iconName="Download" 
                  iconPosition="left"
                  onClick={handleExportReports}
                >
                  Export Reports
                </Button>
                <Button 
                  variant="default" 
                  iconName="Plus" 
                  iconPosition="left"
                  onClick={handleNewVATReturn}
                >
                  New VAT Return
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <nav className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Compliance Alerts */}
              {loadingStates?.alerts ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-3 bg-muted rounded"></div>
                      <div className="h-3 bg-muted rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <ComplianceAlert
                  alerts={complianceAlerts}
                  onDismiss={handleDismissAlert}
                  onViewAll={() => showNotificationMessage('Opening all compliance alerts')}
                />
              )}

              {/* VAT Returns Grid */}
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Current VAT Returns</h2>
                {loadingStates?.vatReturns ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3]?.map(i => (
                      <div key={i} className="bg-card border border-border rounded-lg p-6">
                        <div className="animate-pulse">
                          <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
                          <div className="h-8 bg-muted rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-muted rounded w-1/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : vatReturns?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vatReturns?.slice(0, 3)?.map((vatReturn) => (
                      <VATReturnCard
                        key={vatReturn?.id}
                        period={vatReturn?.period}
                        status={vatReturn?.status}
                        dueDate={vatReturn?.dueDate}
                        netLiability={vatReturn?.netLiability}
                        inputVAT={vatReturn?.inputVAT || 0}
                        outputVAT={vatReturn?.outputVAT || 0}
                        completionPercentage={vatReturn?.completionPercentage}
                        missingItems={vatReturn?.missingItems}
                        onViewDetails={() => handleVATReturnAction(vatReturn?.id, 'view')}
                        onFile={() => handleVATReturnAction(vatReturn?.id, 'file')}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No VAT Returns</h3>
                    <p className="text-muted-foreground">No VAT return data available. Create transactions to generate VAT returns.</p>
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Returns</p>
                      <p className="text-2xl font-bold text-foreground">
                        {loadingStates?.vatReturns ? '-' : vatReturns?.filter(v => v?.status === 'ready' || v?.status === 'overdue')?.length || 0}
                      </p>
                    </div>
                    <Icon name="Clock" size={24} className="text-warning" />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Due</p>
                      <p className="text-2xl font-bold text-foreground">
                        {loadingStates?.vatReturns ? '-' : `$${(vatReturns?.reduce((sum, v) => sum + (v?.status !== 'filed' ? v?.netLiability : 0), 0) || 0)?.toLocaleString()}`}
                      </p>
                    </div>
                    <Icon name="DollarSign" size={24} className="text-error" />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Filed This Year</p>
                      <p className="text-2xl font-bold text-foreground">
                        {loadingStates?.vatReturns ? '-' : vatReturns?.filter(v => v?.status === 'filed')?.length || 0}
                      </p>
                    </div>
                    <Icon name="CheckCircle" size={24} className="text-success" />
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Compliance Score</p>
                      <p className="text-2xl font-bold text-foreground">
                        {loadingStates?.vatReturns || loadingStates?.alerts ? '-' : (() => {
                          const totalReturns = vatReturns?.length || 0;
                          const filedReturns = vatReturns?.filter(v => v?.status === 'filed')?.length || 0;
                          const alertCount = complianceAlerts?.length || 0;
                          
                          if (totalReturns === 0) return '100%';
                          
                          // Calculate compliance based on filed returns and alert severity
                          const filingScore = totalReturns > 0 ? (filedReturns / totalReturns) * 100 : 100;
                          const alertPenalty = Math.min(alertCount * 5, 30); // Max 30% penalty for alerts
                          const complianceScore = Math.max(filingScore - alertPenalty, 0);
                          
                          return `${Math.round(complianceScore)}%`;
                        })()}
                      </p>
                    </div>
                    <Icon name="TrendingUp" size={24} className="text-success" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'returns' && (
            <div className="space-y-6">
              {loadingStates?.calculations ? (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="animate-pulse">
                    <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="h-4 bg-muted rounded"></div>
                      <div className="h-4 bg-muted rounded"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <VATCalculationSummary
                  period={vatCalculations?.period || complianceCenterService?.getCurrentQuarter()}
                  calculations={vatCalculations}
                  onRecalculate={handleRecalculateVAT}
                />
              )}
              
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">All VAT Returns</h2>
                {loadingStates?.vatReturns ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[1, 2, 3, 4]?.map(i => (
                      <div key={i} className="bg-card border border-border rounded-lg p-6">
                        <div className="animate-pulse">
                          <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
                          <div className="h-8 bg-muted rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-muted rounded w-1/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : vatReturns?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {vatReturns?.map((vatReturn) => (
                      <VATReturnCard
                        key={vatReturn?.id}
                        period={vatReturn?.period}
                        status={vatReturn?.status}
                        dueDate={vatReturn?.dueDate}
                        netLiability={vatReturn?.netLiability}
                        inputVAT={vatReturn?.inputVAT || 0}
                        outputVAT={vatReturn?.outputVAT || 0}
                        completionPercentage={vatReturn?.completionPercentage}
                        missingItems={vatReturn?.missingItems}
                        onViewDetails={() => handleVATReturnAction(vatReturn?.id, 'view')}
                        onFile={() => handleVATReturnAction(vatReturn?.id, 'file')}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No VAT Returns</h3>
                    <p className="text-muted-foreground">No VAT return data available. Create invoices and bills to generate VAT returns.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <TransactionReviewTable
              transactions={transactions}
              onTransactionClick={handleTransactionClick}
              onBulkAction={handleBulkAction}
              loading={loadingStates?.transactions}
            />
          )}

          {activeTab === 'ai-checker' && (
            <AIComplianceChecker
              insights={aiInsights}
              onRunCheck={handleRunAICheck}
              onViewDetails={handleViewInsightDetails}
              isLoading={isAICheckLoading || loadingStates?.insights}
            />
          )}

          {activeTab === 'filing-status' && (
            <FilingStatusTracker
              filings={filings}
              onViewReceipt={handleViewReceipt}
              onDownloadReturn={handleDownloadReturn}
              loading={loadingStates?.filings}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ComplianceCenter;